import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import type {
  ShowMetaSource,
  ShowMetaTagValues,
  ShowMetaTarget,
} from "@/lib/showMetaTags";
import {
  SEO_TITLE_SUFFIX_KEY,
  deriveShowMetaTags,
  showMetaInput,
} from "@/lib/showMetaTags";

const DEFAULT_TITLE = "Shorts";

const upsertMeta = (
  attr: "name" | "property",
  key: string,
  content: string | null,
) => {
  const existing = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`,
  );

  if (!content) {
    // Only remove elements this hook created — a pre-existing static tag
    // (e.g. a future default in index.html) must survive navigation.
    if (existing?.dataset.showMeta !== undefined) existing.remove();
    return;
  }

  const el = existing ?? document.createElement("meta");
  el.setAttribute(attr, key);
  el.setAttribute("content", content);
  if (!existing) {
    el.dataset.showMeta = "";
    document.head.appendChild(el);
  }
};

const upsertCanonical = (href: string | null) => {
  const existing = document.head.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]',
  );

  if (!href) {
    // Only remove elements this hook created — see upsertMeta.
    if (existing?.dataset.showMeta !== undefined) existing.remove();
    return;
  }

  const el = existing ?? document.createElement("link");
  el.setAttribute("rel", "canonical");
  el.setAttribute("href", href);
  if (!existing) {
    el.dataset.showMeta = "";
    document.head.appendChild(el);
  }
};

const upsertJsonLd = (data: ShowMetaTagValues["jsonLd"] | null) => {
  // Scoped to data-show-meta: there is no natural key like meta's
  // name/property to look one up by, so the mark is the only handle. It
  // matches the prerendered script too (vite/prerenderShows.ts emits the same
  // mark) — deliberately: without that, this appends a SECOND TVSeries block
  // on every prerendered page, one frozen in English and one localized.
  const existing = document.head.querySelector<HTMLScriptElement>(
    'script[type="application/ld+json"][data-show-meta]',
  );

  if (!data) {
    existing?.remove();
    return;
  }

  const el = existing ?? document.createElement("script");
  el.type = "application/ld+json";
  el.textContent = JSON.stringify(data);
  if (!existing) {
    el.dataset.showMeta = "";
    document.head.appendChild(el);
  }
};

export const applyShowMetaTags = (
  tags: ReturnType<typeof deriveShowMetaTags> | null,
) => {
  document.title = tags?.title ?? DEFAULT_TITLE;
  upsertMeta("name", "description", tags?.description ?? null);
  upsertMeta("property", "og:title", tags?.ogTitle ?? null);
  upsertMeta("property", "og:description", tags?.ogDescription ?? null);
  upsertMeta("property", "og:image", tags?.ogImage ?? null);
  upsertMeta("property", "og:video:image", tags?.ogVideoImage ?? null);
  upsertMeta("property", "og:type", tags?.ogType ?? null);
  upsertMeta("property", "og:url", tags?.ogUrl ?? null);
  upsertMeta("name", "twitter:card", tags?.twitterCard ?? null);
  upsertMeta("name", "robots", tags?.robots ?? null);
  upsertCanonical(tags?.canonicalUrl ?? null);
  upsertJsonLd(tags?.jsonLd ?? null);
};

/**
 * Injects per-show SEO/social tags into <head> once the show's data
 * resolves, and tears them down on unmount — SPA routes share one <head>,
 * so a stale show's tags would otherwise leak into the next route (SHO-664).
 * Also owns the JSON-LD script: a schema.org/TVSeries for rich-results
 * eligibility on the series page, a VideoObject carrying the video thumbnail
 * on the player (SHO-725, SHO-757).
 *
 * This is the *runtime* half of the two SEO layers: the browser tab, Google's
 * rendered indexing, and the localized values — it is what a show reached by
 * SPA navigation, or published since the last deploy, gets. Raw-HTML unfurling
 * (Discord, X, Meta, Slack, iMessage — none of them run JS) is the *build-time*
 * half, `vite/prerenderShows.ts`. On a prerendered page both are live at once,
 * which is why the plugin marks its own tags `data-show-meta` too: the upserts
 * below then adopt those elements instead of shadowing them with a second,
 * unmarked set the teardown would leak into the next route.
 */
const useShowMetaTags = (
  show: ShowMetaSource | undefined,
  target: ShowMetaTarget,
) => {
  const { t } = useTranslation();
  const titleSuffix = t(SEO_TITLE_SUFFIX_KEY[target]);

  useEffect(() => {
    if (!show) return;

    applyShowMetaTags(
      deriveShowMetaTags(
        showMetaInput(show, target, window.location.origin, titleSuffix),
      ),
    );

    return () => applyShowMetaTags(null);
  }, [show, target, titleSuffix]);
};

export default useShowMetaTags;
